import { FormHelperText, Icon, IconButton } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiTypography from "components/base/UiTypography";
import { CustomGetCompaniesForDropdownDocument } from "generated/graphql";
import usePaginatedCompanyLookup from "hooks/companies/usePaginatedCompanyLookup";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useEffect } from "react";
import {
  ConfirmProposalModalFormValues,
  confirmProposalDropdownInnerSx,
} from "./ConfirmProposalModal.shared";
import { draftColors } from "./draftDesignTokens";

type PackageRecipientsProps = {
  enabled: boolean;
};

type RecipientCompanyLookupItem = {
  id: string;
  name: string;
  contacts?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email?: string | null;
  }>;
};

function PackageRecipientRow({
  index,
  companyId,
  onRemove,
  enabled,
}: {
  index: number;
  companyId?: string | null;
  onRemove: () => void;
  enabled: boolean;
}) {
  const { control, setValue } = useFormContext<ConfirmProposalModalFormValues>();

  const {
    items: companiesData,
    loading: companiesLoading,
    search,
    loadInitialPage,
    loadMore,
    resetSearch,
  } = usePaginatedCompanyLookup<RecipientCompanyLookupItem>(
    CustomGetCompaniesForDropdownDocument,
    companyId ? [companyId] : [],
    enabled
  );

  useEffect(() => {
    if (!enabled) {
      resetSearch();
    }
  }, [enabled, resetSearch]);

  const companyOptions =
    companiesData?.map((company) => ({
      label: company.name,
      value: company.id,
    })) ?? [];

  const selectedCompany = companiesData?.find((company) => company.id === companyId);
  const contactOptions =
    selectedCompany?.contacts?.map((contact) => ({
      label: `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim() || contact.email,
      value: contact.id,
    })) ?? [];

  return (
    <MDBox display="grid" gridTemplateColumns="1fr 1fr auto" gap="8px">
      <MDBox
        display="flex"
        flexDirection="column"
        gap="4px"
        p="6px 8px 6px"
        borderRadius="8px"
        border={`1px solid ${draftColors.border}`}
      >
        <UiTypography uiVariant="label-xs">Select Company</UiTypography>
        <MDBox sx={confirmProposalDropdownInnerSx}>
          <Controller
            control={control}
            name={`packageRecipients.${index}.companyId`}
            render={({ field: controllerField, fieldState }) => (
              <AutoComplete
                field={
                  {
                    ...controllerField,
                    onChange: (value: string | null) => {
                      controllerField.onChange(value);
                      setValue(`packageRecipients.${index}.contactId`, "");
                    },
                  } as never
                }
                loading={companiesLoading}
                search={search}
                onOpen={loadInitialPage}
                onClose={resetSearch}
                onEndReached={loadMore}
                error={fieldState.error?.message}
                options={companyOptions}
                placeholder="Select Company"
              />
            )}
          />
        </MDBox>
      </MDBox>
      <MDBox
        display="flex"
        flexDirection="column"
        gap="4px"
        p="6px 8px 6px"
        borderRadius="8px"
        border={`1px solid ${draftColors.border}`}
      >
        <UiTypography uiVariant="label-xs">Select Contact</UiTypography>
        <MDBox sx={confirmProposalDropdownInnerSx}>
          <Controller
            control={control}
            name={`packageRecipients.${index}.contactId`}
            render={({ field: controllerField, fieldState }) => (
              <AutoComplete
                field={controllerField as never}
                loading={false}
                error={fieldState.error?.message}
                options={contactOptions}
                placeholder="Select Contact"
                disabled={!companyId}
              />
            )}
          />
        </MDBox>
      </MDBox>
      <IconButton size="small" onClick={onRemove} sx={{ alignSelf: "center", p: "2px" }}>
        <Icon sx={{ fontSize: "18px !important", color: draftColors.red }}>delete</Icon>
      </IconButton>
    </MDBox>
  );
}

export default function PackageRecipients({ enabled }: PackageRecipientsProps) {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<ConfirmProposalModalFormValues>();
  const sendToMultipleCompanies = watch("sendToMultipleCompanies");
  const packageRecipients = watch("packageRecipients");
  const { fields, append, remove } = useFieldArray({
    control,
    name: "packageRecipients",
  });

  if (!sendToMultipleCompanies) {
    return null;
  }

  return (
    <MDBox display="flex" flexDirection="column" gap="12px">
      {fields.map((field, index) => (
        <PackageRecipientRow
          key={field.id}
          index={index}
          companyId={packageRecipients?.[index]?.companyId}
          onRemove={() => remove(index)}
          enabled={enabled}
        />
      ))}
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <UiButton uiVariant="outlined" onClick={() => append({ companyId: "", contactId: "" })}>
          Add Company
        </UiButton>
      </MDBox>
      {errors.packageRecipients?.message && (
        <FormHelperText sx={{ color: "red", mt: "-4px" }}>
          {errors.packageRecipients.message as string}
        </FormHelperText>
      )}
    </MDBox>
  );
}
